/**
 * Map API / validation messages to plain English for salon staff (Phase 8).
 *
 * @param {string | undefined | null} message
 * @returns {string | null}
 */
export function toPlainEnglishError(message) {
  if (!message || typeof message !== 'string') {
    return null
  }

  const trimmed = message.trim()
  const lower = trimmed.toLowerCase()

  const rules = [
    [/validation failed/i, 'Please check the form and try again.'],
    [/unauthenticated/i, 'Please sign in again.'],
    [/unauthorized|forbidden|403/i, 'You do not have permission to do that.'],
    [/not found|404/i, 'We could not find that record. It may have been removed.'],
    [/too many attempts/i, 'Too many tries. Wait a minute and try again.'],
    [/password.*expired/i, 'Your password has expired. Please set a new one in Settings.'],
    [/screen is locked/i, 'Unlock the screen with your password first.'],
    [/branch.*required|select a branch/i, 'Choose your salon branch and try again.'],
    [/payment total must equal/i, 'The payment amount must match the bill total.'],
    [/insufficient stock|not enough stock/i, 'Not enough product in stock for this sale.'],
    [/day.*locked|date.*locked/i, 'This day is closed for sales. Ask the owner to unlock it.'],
    [/stylist.*not available|conflict/i, 'That time is already booked. Pick another time or staff member.'],
    [/slot.*unavailable|no longer available/i, 'That time is no longer free. Please choose another slot.'],
    [/customer.*required/i, 'Please choose a customer first.'],
    [/phone.*already|duplicate.*phone/i, 'A customer with this phone number already exists.'],
    [/email.*already|duplicate.*email/i, 'This email is already in use.'],
    [/invalid credentials|auth\.failed/i, 'Email or password is wrong. Try again.'],
    [/inactive|deactivated/i, 'This account is turned off. Ask the owner for help.'],
    [/network error|connection refused|err_network/i, 'Cannot reach the server. Check your internet and that the app is running.'],
    [/the name field is required/i, 'Please enter a name.'],
    [/the phone field is required/i, 'Please enter a phone number.'],
    [/the email field is required/i, 'Please enter an email address.'],
    [/must be a valid email/i, 'Please enter a valid email address.'],
    [/must be at least \d+ characters/i, 'Please use a longer password.'],
  ]

  for (const [pattern, plain] of rules) {
    if (pattern.test(lower) || pattern.test(trimmed)) {
      return plain
    }
  }

  return null
}

/**
 * @param {unknown} err
 * @param {string} fallback
 * @returns {string}
 */
export function resolvePlainError(err, fallback) {
  const axiosErr = /** @type {{ response?: { data?: { message?: string, errors?: Record<string, string[]> } } }} */ (
    err
  )

  const fieldErrors = axiosErr?.response?.data?.errors
  if (fieldErrors && typeof fieldErrors === 'object') {
    const firstField = Object.values(fieldErrors).flat().find(Boolean)
    if (typeof firstField === 'string') {
      return toPlainEnglishError(firstField) ?? firstField
    }
  }

  const message = axiosErr?.response?.data?.message
  if (typeof message === 'string') {
    return toPlainEnglishError(message) ?? message
  }

  return fallback
}
